import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { Plugin, PluginKey, NodeSelection } from '@tiptap/pm/state'
import { PlaceholderNodeView } from './placeholder-node-view'
import type { PlaceholderOptions } from './types'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    placeholder: {
      /**
       * Insert a simple text placeholder
       */
      insertPlaceholder: (options: { value: string }) => ReturnType
      /**
       * Insert an HTML placeholder
       */
      insertHtmlPlaceholder: (options: { html: string; label: string }) => ReturnType
    }
  }
}

export default Node.create<PlaceholderOptions>({
  name: 'placeholder',
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addOptions() {
    return {
      types: [],
      brackets: {
        open: '{{',
        close: '}}',
      },
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      name: {
        default: '',
        parseHTML: element => {
          if (element.classList.contains('placeholder')
              || element.classList.contains('placeholder-html')
              || element.getAttribute('data-placeholder-html') === 'true') {
            return element.getAttribute('data-placeholder-name')
          }
          return null
        },
        renderHTML: attributes => {
          if (attributes.name) {
            return { 'data-placeholder-name': attributes.name }
          }
          return {}
        },
      },
      html: {
        default: null,
        parseHTML: element => {
          if (element.classList.contains('placeholder-html')
              || element.getAttribute('data-placeholder-html') === 'true') {
            return element.getAttribute('data-placeholder-content') || null
          }
          return null
        },
        renderHTML: attributes => {
          if (attributes.html) {
            return { 'data-placeholder-content': attributes.html }
          }
          return {}
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span.placeholder',
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false

          if (!element.classList.contains('placeholder') || !element.hasAttribute('data-placeholder-name')) {
            return false
          }

          const textContent = element.textContent || ''
          const { open, close } = this.options.brackets

          if (textContent.startsWith(open) && textContent.endsWith(close)) {
            const name = textContent.slice(open.length, -close.length)
            return { name }
          }

          return false
        },
      },
      {
        tag: 'span.placeholder-html',
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false

          if (!element.classList.contains('placeholder-html')
              || !element.hasAttribute('data-placeholder-html')
              || !element.hasAttribute('data-placeholder-name')) {
            return false
          }

          const html = element.getAttribute('data-placeholder-content') || element.innerHTML
          const name = element.getAttribute('data-placeholder-name') || 'html'

          return { name, html }
        },
      },
      {
        tag: 'span[data-placeholder-html="true"]',
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false

          if (element.getAttribute('data-placeholder-html') !== 'true'
              || !element.hasAttribute('data-placeholder-name')) {
            return false
          }

          const html = element.getAttribute('data-placeholder-content') || element.innerHTML
          const name = element.getAttribute('data-placeholder-name') || 'html'

          return { name, html }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    const { name, html } = node.attrs
    const { open, close } = this.options.brackets

    if (html) {
      // HTML placeholder - create a custom DOM element with the HTML content
      // This approach allows us to serialize the HTML properly while maintaining NodeView in editor
      return {
        dom: (() => {
          const span = document.createElement('span')
          Object.entries(mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
            'class': 'placeholder-html',
            'data-placeholder-html': 'true',
            'data-placeholder-name': name,
            'data-placeholder-content': html,
          })).forEach(([key, value]) => {
            span.setAttribute(key, value as string)
          })
          span.innerHTML = html
          return span
        })(),
      }
    }

    // Text placeholder
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'class': 'placeholder',
        'data-placeholder-name': name,
      }),
      `${open}${name}${close}`,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(PlaceholderNodeView, {
      // Allow all events to be handled by the NodeView
      stopEvent: () => false,
    })
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('placeholderDrag'),
        props: {
          handleDOMEvents: {
            drop: (view, event) => {
              let data = event.dataTransfer?.getData('application/x-placeholder')
              if (!data) {
                data = event.dataTransfer?.getData('text/plain')
              }

              if (!data) return false

              try {
                const dragData = JSON.parse(data)

                if (dragData.type !== 'placeholder-move') return false

                const dropPos = view.posAtCoords({ left: event.clientX, top: event.clientY })
                if (!dropPos) return false

                event.preventDefault()

                const { tr, schema } = view.state
                const dragNode = tr.doc.nodeAt(dragData.pos)

                if (dragNode && dragNode.type.name === 'placeholder') {
                  const newNode = schema.nodes.placeholder.create({
                    name: dragData.name,
                    html: dragData.html,
                  })

                  tr.delete(dragData.pos, dragData.pos + dragNode.nodeSize)

                  const adjustedPos = dropPos.pos > dragData.pos
                    ? dropPos.pos - dragNode.nodeSize
                    : dropPos.pos

                  tr.insert(adjustedPos, newNode)

                  view.dispatch(tr)

                  setTimeout(() => {
                    if (view.isDestroyed) return

                    const selection = NodeSelection.create(view.state.doc, adjustedPos)
                    const tr = view.state.tr.setSelection(selection)
                    view.dispatch(tr)
                  }, 10)

                  return true
                }
              } catch {
                // swallow
              }

              return false
            },

            dragover: (view, event) => {
              // For placeholder drag and drop, we need to allow the dragover
              // We can't read the data during dragover, so we check the effectAllowed
              if (event.dataTransfer?.effectAllowed === 'move') {
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
                return true
              }

              return false
            },
          },
        },
      }),
    ]
  },

  addCommands() {
    return {
      insertPlaceholder:
        (options) =>
          ({ commands }) => {
            return commands.insertContent({
              type: this.name,
              attrs: {
                name: options.value,
              },
            })
          },
      insertHtmlPlaceholder:
        (options) =>
          ({ commands }) => {
            return commands.insertContent({
              type: this.name,
              attrs: {
                name: options.label,
                html: options.html,
              },
            })
          },
    }
  },
})
